import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import {
  DisplayName,
  DisplayProject,
  DisplayStatus,
  DisplayType,
  DisplayPriority,
} from "../../components/DataComponents";
import {
  DataSection,
  ActionsSection,
  ButtonsSection,
  SubSection,
  DescriptionSection,
} from "../../../../components/data-components/sections/DataSections";
import {
  EditJob,
  DeleteJob,
  TakeJob,
  ViewJob as ViewJobPermission,
  ViewJobFromAssignedProject,
  ChangeJobStatus,
  ManageDeletedData,
  EditAssignedJob,
  TakeJobFromAssignedProject,
  ChangeJobStatusFromAssignedProject,
  DeleteAssignedJob,
} from "../../../../services/authorization/Permissions";
import DisplayAssignedUser from "../../../../components/data-components/DisplayAssignedUser";
import DisplayAvailableFrom from "../../../../components/data-components/DisplayAvailableFrom";
import DisplayDeadline from "../../../../components/data-components/DisplayDeadline";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import DeleteButton from "../../../../components/buttons/delete-button/DeleteButton";
import EditButton from "../../../../components/buttons/edit-button/EditButton";
import PageLayout from "../../../_layout/PageLayout";
import { CurrentJobStorageModel } from "../../../../services/job/CurrentJobStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { JobResponse } from "../../../../api-models/response/job/JobResponse";
import { JobMenuItemsModel } from "../../JobMenuItems";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { TakeJobButton } from "../../components/buttons/TakeJobButton";
import { MarkAsInProgressButton } from "../../components/buttons/MarkAsInProgressButton";
import { MarkAsDoneButton } from "../../components/buttons/MarkAsDoneButton";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { JobStatus } from "../../../../generated/Jobs";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const currentJobId = urlParamsService.getJobId()!;

  const [currentJobStorage] = useState(() =>
    CurrentJobStorageModel.create({ currentJobId: currentJobId }, { api })
  );

  const jobMenuItems = useMemo(() => {
    return JobMenuItemsModel.create({
      jobId: currentJobId,
      isLoading: currentJobStorage.isLoading,
      numberOfSewerObjects: currentJobStorage.currentJob.sewerObjectsCount,
    });
  }, [currentJobId, currentJobStorage.currentJob.sewerObjectsCount, currentJobStorage.isLoading]);

  const currentJob: JobResponse = useMemo(() => {
    return currentJobStorage.currentJob;
  }, [currentJobStorage.currentJob]);

  const canUserEditJob = useMemo(() => {
    if (permissionsService.userHasPermissionTo(EditJob)) return true;
    if (
      permissionsService.userHasPermissionTo(EditAssignedJob) &&
      currentJobStorage.currentUserIsAssignedToJob
    )
      return true;
    return false;
  }, [currentJobStorage.currentUserIsAssignedToJob]);

  const canUserTakeJob = useMemo(() => {
    if (permissionsService.userHasOneOfThePermissions([TakeJob, TakeJobFromAssignedProject]))
      return true;

    return false;
  }, []);

  const canUserDeleteJob = useMemo(() => {
    if (permissionsService.userHasPermissionTo(DeleteJob)) return true;
    if (
      permissionsService.userHasPermissionTo(DeleteAssignedJob) &&
      currentJobStorage.currentUserIsAssignedToJob
    )
      return true;
    return false;
  }, [currentJobStorage.currentUserIsAssignedToJob]);

  const menuItems = {
    staticItems: jobMenuItems.getStaticItems(),
  };

  const isMarkJobAsInProgressButtonDisabled: boolean = useMemo(() => {
    return (
      !permissionsService.userHasOneOfThePermissions([
        ChangeJobStatus,
        ChangeJobStatusFromAssignedProject,
      ]) ||
      (permissionsService.userHasPermissionTo(ChangeJobStatusFromAssignedProject) &&
        !currentJobStorage.currentUserIsAssignedToJob) ||
      (currentJob.status === JobStatus.ToDo && !currentJobStorage.currentUserIsAssignedToJob)
    );
  }, [currentJob.status, currentJobStorage.currentUserIsAssignedToJob]);

  const isMarkJobAsDoneButtonDisabled: boolean = useMemo(() => {
    return (
      !permissionsService.userHasOneOfThePermissions([
        ChangeJobStatus,
        ChangeJobStatusFromAssignedProject,
      ]) ||
      (permissionsService.userHasPermissionTo(ChangeJobStatusFromAssignedProject) &&
        !currentJobStorage.currentUserIsAssignedToJob) ||
      (currentJob.status === JobStatus.InProgress && !currentJobStorage.currentUserIsAssignedToJob)
    );
  }, [currentJob.status, currentJobStorage.currentUserIsAssignedToJob]);

  const isTakeJobButtonVisible: boolean = useMemo(() => {
    return (
      (!currentJob.assignedUser || !!!currentJob.assignedUser.id) &&
      currentJob.status === JobStatus.ToDo
    );
  }, [currentJob]);

  const isMarkJobAsInProgressButtonVisible: boolean = useMemo(() => {
    return (
      currentJob.assignedUser != null &&
      !!currentJob.assignedUser.id &&
      currentJob.status === JobStatus.ToDo
    );
  }, [currentJob]);

  const isMarkJobAsDoneButtonVisible: boolean = useMemo(() => {
    return (
      currentJob.assignedUser != null &&
      !!currentJob.assignedUser.id &&
      currentJob.status === JobStatus.InProgress
    );
  }, [currentJob]);

  const handleEditClick = () => {
    history.push(`/jobs/${currentJob.id}/edit`);
  };

  const handleDeleteJob = async () => {
    try {
      await currentJobStorage.deleteJob();
      history.push("/jobs");
      snackbarNotificationsService.showSuccess("ViewJob.delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.job.delete");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([{
      entity: EntityTypeFromPath.Job,
      name: `${currentJobStorage.currentJob.name}`
    }]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentJobStorage.currentJob.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewJobPermission, ViewJobFromAssignedProject]}
      titleKey="ViewJob.title"
      titleContext={{ jobName: currentJob.name }}
      menuItems={menuItems}
      isLoading={currentJobStorage.isLoading}
      id="view-job"
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton onClick={handleEditClick} disabled={!canUserEditJob} />
          <TakeJobButton
            isHidden={!isTakeJobButtonVisible}
            disabled={!canUserTakeJob}
            currentJobStorage={currentJobStorage}
          />
          <MarkAsInProgressButton
            isHidden={!isMarkJobAsInProgressButtonVisible}
            disabled={isMarkJobAsInProgressButtonDisabled}
            currentJobStorage={currentJobStorage}
          />
          <MarkAsDoneButton
            isHidden={!isMarkJobAsDoneButtonVisible}
            disabled={isMarkJobAsDoneButtonDisabled}
            currentJobStorage={currentJobStorage}
          />
          <DeleteButton
            disabled={!canUserDeleteJob}
            confirmationModalTitleKey="ViewJob.delete"
            confirmationModalSubtitleKey="ViewJob.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
            confirmationAction={handleDeleteJob}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataSection labelKey="general.jobs.details">
        <SubSection>
          <DisplayName name={currentJob.name} />
          <DisplayProject project={currentJob.project} />
          <DisplayStatus status={currentJob.status} />
        </SubSection>
        <SubSection>
          <DisplayType type={currentJob.type} />
          <DisplayAssignedUser assignedUser={currentJob.assignedUser} />
          <DisplayPriority priority={currentJob.priority} />
        </SubSection>
        <SubSection>
          <DisplayAvailableFrom date={currentJob.availableFrom} />
          <DisplayDeadline date={currentJob.deadline} />
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentJob.created} />
          <DisplayCreatedBy createdBy={currentJob.createdBy.name} />
        </SubSection>
      </DataSection>
      <DescriptionSection description={currentJob.description} />
    </PageLayout>
  );
});
